import { useState, useCallback } from "react";

const STORAGE_KEY = "wizard-sounds-favorites";
const COMBO_FAVORITES_KEY = "wizard-sounds-combo-favorites";
const RECENTS_KEY = "wizard-sounds-recents";
const MAX_RECENTS = 50;

function loadSet(key: string): Set<string> {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return new Set(JSON.parse(stored));
  } catch {}
  return new Set();
}

function saveSet(key: string, set: Set<string>) {
  localStorage.setItem(key, JSON.stringify([...set]));
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<Set<string>>(() => loadSet(STORAGE_KEY));
  const [comboFavorites, setComboFavorites] = useState<Set<string>>(() => loadSet(COMBO_FAVORITES_KEY));
  const [recents, setRecents] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(RECENTS_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return [];
  });

  const toggleFavorite = useCallback((soundId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(soundId)) {
        next.delete(soundId);
      } else {
        next.add(soundId);
      }
      saveSet(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const toggleComboFavorite = useCallback((comboId: string) => {
    setComboFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(comboId)) {
        next.delete(comboId);
      } else {
        next.add(comboId);
      }
      saveSet(COMBO_FAVORITES_KEY, next);
      return next;
    });
  }, []);

  const addRecent = useCallback((soundId: string) => {
    setRecents((prev) => {
      const next = [soundId, ...prev.filter((id) => id !== soundId)].slice(
        0,
        MAX_RECENTS,
      );
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const isFavorite = useCallback(
    (soundId: string) => favorites.has(soundId),
    [favorites],
  );

  const isComboFavorite = useCallback(
    (comboId: string) => comboFavorites.has(comboId),
    [comboFavorites],
  );

  return { favorites, comboFavorites, recents, toggleFavorite, toggleComboFavorite, addRecent, isFavorite, isComboFavorite };
}
